import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Copy, Upload, Hash, Globe, Mail, Link, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

const IOCExtractor = () => {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('text');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const extractFromText = async () => {
    if (!text.trim()) {
      toast.error('Please enter some text');
      return;
    }

    setLoading(true);
    try {
      // Create a blob from the text and send it as a file
      const blob = new Blob([text], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', blob, 'input.txt');

      const response = await axios.post(`${OSINT_BACKEND}/api/osint/extractor/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        
      });

      setExtracted(response.data);
      toast.success(`Extracted ${response.data.statistics?.total || 0} IOCs`);
    } catch (error) {
      console.error('Extraction error:', error);
      // Fallback to client-side extraction
      extractClientSide(text);
    } finally {
      setLoading(false);
    }
  };

  const extractFromFile = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${OSINT_BACKEND}/api/osint/extractor/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        
      });

      setExtracted(response.data);
      toast.success(`Extracted ${response.data.statistics?.total || 0} IOCs`);
    } catch (error) {
      console.error('Extraction error:', error);
      // Read file and do client-side extraction
      const reader = new FileReader();
      reader.onload = (e) => {
        extractClientSide(e.target.result);
      };
      reader.readAsText(file);
    } finally {
      setLoading(false);
    }
  };

  const extractClientSide = (inputText) => {
    const ipRegex = /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g;
    const domainRegex = /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]\b/gi;
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const urlRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b(?:[-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi;
    const md5Regex = /\b[a-fA-F0-9]{32}\b/g;
    const sha1Regex = /\b[a-fA-F0-9]{40}\b/g;
    const sha256Regex = /\b[a-fA-F0-9]{64}\b/g;

    const ips = [...new Set(inputText.match(ipRegex) || [])];
    const domains = [...new Set(inputText.match(domainRegex) || [])].filter(d => !d.match(ipRegex));
    const emails = [...new Set(inputText.match(emailRegex) || [])];
    const urls = [...new Set(inputText.match(urlRegex) || [])];
    const md5 = [...new Set(inputText.match(md5Regex) || [])];
    const sha1 = [...new Set(inputText.match(sha1Regex) || [])];
    const sha256 = [...new Set(inputText.match(sha256Regex) || [])];

    const results = {
      ips,
      domains,
      emails,
      urls,
      md5,
      sha1,
      sha256,
      statistics: {
        ips: ips.length,
        domains: domains.length,
        emails: emails.length,
        urls: urls.length,
        md5: md5.length,
        sha1: sha1.length,
        sha256: sha256.length,
        total: ips.length + domains.length + emails.length + urls.length + md5.length + sha1.length + sha256.length
      }
    };

    setExtracted(results);
    toast.success(`Extracted ${results.statistics.total} IOCs`);
  };

  const copyToClipboard = (items, type) => {
    navigator.clipboard.writeText(items.join('\n'));
    toast.success(`Copied ${items.length} ${type} to clipboard`);
  };

  const copyAllToClipboard = () => {
    if (!extracted) return;
    
    const allItems = [
      ...extracted.ips || [],
      ...extracted.domains || [],
      ...extracted.emails || [],
      ...extracted.urls || [],
      ...extracted.md5 || [],
      ...extracted.sha1 || [],
      ...extracted.sha256 || []
    ];
    
    navigator.clipboard.writeText(allItems.join('\n'));
    toast.success(`Copied ${allItems.length} IOCs to clipboard`);
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'ips': return <Globe className="w-4 h-4" />;
      case 'domains': return <Globe className="w-4 h-4" />;
      case 'emails': return <Mail className="w-4 h-4" />;
      case 'urls': return <Link className="w-4 h-4" />;
      case 'md5':
      case 'sha1':
      case 'sha256': return <Hash className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      ips: 'IP Addresses',
      domains: 'Domains',
      emails: 'Email Addresses',
      urls: 'URLs',
      md5: 'MD5 Hashes',
      sha1: 'SHA1 Hashes',
      sha256: 'SHA256 Hashes'
    };
    return labels[type] || type;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-3xl font-bold font-heading mb-2" data-testid="ioc-extractor-title">IOC Extractor</h2>
        <p className="text-muted-foreground">Extract indicators of compromise from unstructured text or files</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="text" data-testid="text-input-tab">
            <FileText className="w-4 h-4 mr-2" />
            Paste Text
          </TabsTrigger>
          <TabsTrigger value="file" data-testid="file-upload-tab">
            <Upload className="w-4 h-4 mr-2" />
            Upload File
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Paste Text</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste logs, threat reports, or any text containing IOCs...

Example:
The attacker used IP 192.168.1.100 to communicate with C2 server at evil.com.
Malicious file hash: d41d8cd98f00b204e9800998ecf8427e
Phishing email sent from attacker@malicious.com containing link to https://phishing-site.com/login"
                className="w-full h-64 p-4 border rounded-lg font-mono text-sm"
                data-testid="text-input"
              />
              <Button 
                onClick={extractFromText} 
                className="w-full mt-4" 
                disabled={loading || !text.trim()}
                data-testid="extract-text-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Extracting...' : 'Extract IOCs'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="file">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Upload File</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".txt,.log,.csv,.json,.xml,.html,.md"
                  onChange={handleFileChange}
                  className="hidden"
                  id="ioc-file"
                  data-testid="file-input"
                />
                <label htmlFor="ioc-file" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm font-medium mb-2">
                    {file ? file.name : 'Click to select a file'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported: .txt, .log, .csv, .json, .xml, .html, .md
                  </p>
                </label>
              </div>
              <Button 
                onClick={extractFromFile} 
                className="w-full mt-4" 
                disabled={loading || !file}
                data-testid="extract-file-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Extracting...' : 'Extract IOCs from File'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Results */}
      {extracted && (
        <div className="space-y-4" data-testid="extraction-results">
          {/* Statistics Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Extraction Complete
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyAllToClipboard}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {Object.entries(extracted.statistics || {}).filter(([key]) => key !== 'total').map(([key, value]) => (
                  <div key={key} className="p-3 bg-muted rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary">{value}</div>
                    <div className="text-xs text-muted-foreground capitalize">{key}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-primary/10 rounded-lg text-center">
                <span className="text-lg font-bold">Total: {extracted.statistics?.total || 0} IOCs extracted</span>
              </div>
            </CardContent>
          </Card>

          {/* IOC Type Cards */}
          {['ips', 'domains', 'urls', 'emails', 'md5', 'sha1', 'sha256'].map(type => {
            const items = extracted[type] || [];
            if (items.length === 0) return null;
            
            return (
              <Card key={type}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {getTypeIcon(type)}
                      {getTypeLabel(type)} ({items.length})
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(items, type)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-auto space-y-1">
                    {items.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-2 bg-muted rounded text-sm font-mono hover:bg-muted/80 group"
                      >
                        <span className="break-all">{item}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => {
                            navigator.clipboard.writeText(item);
                            toast.success('Copied to clipboard');
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!extracted && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">No IOCs extracted yet</p>
            <p className="text-sm text-muted-foreground">
              Paste text or upload a file to extract IOCs automatically
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IOCExtractor;
